export interface CalendarSidebarTimedEventLike {
    endsAt: number;
    startsAt: number;
}

export interface CalendarSidebarTimedEventLayout {
    columnCount: number;
    columnIndex: number;
    columnSpan: number;
}

interface IndexedTimedEvent<T extends CalendarSidebarTimedEventLike> extends CalendarSidebarTimedEventLike {
    event: T;
    sourceIndex: number;
}

export function layoutCalendarSidebarTimedEvents<T extends CalendarSidebarTimedEventLike>(
    events: readonly T[],
): Array<T & CalendarSidebarTimedEventLayout> {
    const laidOutEvents = events.map((event) => ({
        ...event,
        columnCount: 1,
        columnIndex: 0,
        columnSpan: 1,
    }));

    const sortedEvents = events
        .map((event, sourceIndex) => ({ event, sourceIndex, startsAt: event.startsAt, endsAt: event.endsAt }))
        .sort((a, b) => a.startsAt - b.startsAt || b.endsAt - a.endsAt);

    let currentGroup: Array<IndexedTimedEvent<T>> = [];
    let currentGroupEnd = -Infinity;

    const flushCurrentGroup = () => {
        if (!currentGroup.length) {
            return;
        }

        applyGroupLayout(currentGroup, laidOutEvents);
        currentGroup = [];
        currentGroupEnd = -Infinity;
    };

    sortedEvents.forEach((event) => {
        if (currentGroup.length && event.startsAt >= currentGroupEnd) {
            flushCurrentGroup();
        }

        currentGroup.push(event);
        currentGroupEnd = Math.max(currentGroupEnd, event.endsAt);
    });

    flushCurrentGroup();

    return laidOutEvents;
}

function applyGroupLayout<T extends CalendarSidebarTimedEventLike>(
    group: Array<IndexedTimedEvent<T>>,
    laidOutEvents: Array<T & CalendarSidebarTimedEventLayout>,
) {
    const columns: Array<Array<IndexedTimedEvent<T>>> = [];
    const columnEndTimes: number[] = [];

    group.forEach((event) => {
        const availableColumnIndex = columnEndTimes.findIndex((columnEndTime) => columnEndTime <= event.startsAt);
        const columnIndex = availableColumnIndex === -1 ? columnEndTimes.length : availableColumnIndex;

        if (availableColumnIndex === -1) {
            columns.push([]);
            columnEndTimes.push(event.endsAt);
        } else {
            columnEndTimes[columnIndex] = event.endsAt;
        }

        columns[columnIndex].push(event);
        laidOutEvents[event.sourceIndex].columnIndex = columnIndex;
    });

    const columnCount = columns.length;

    group.forEach((event) => {
        const laidOutEvent = laidOutEvents[event.sourceIndex];

        laidOutEvent.columnCount = columnCount;
        laidOutEvent.columnSpan = getColumnSpan(event, columns, laidOutEvent.columnIndex);
    });
}

function getColumnSpan<T extends CalendarSidebarTimedEventLike>(
    event: IndexedTimedEvent<T>,
    columns: Array<Array<IndexedTimedEvent<T>>>,
    columnIndex: number,
) {
    let columnSpan = 1;

    for (let nextColumnIndex = columnIndex + 1; nextColumnIndex < columns.length; nextColumnIndex += 1) {
        const hasOverlappingEvent = columns[nextColumnIndex].some((candidate) => eventsOverlap(event, candidate));

        if (hasOverlappingEvent) {
            break;
        }

        columnSpan += 1;
    }

    return columnSpan;
}

function eventsOverlap(a: CalendarSidebarTimedEventLike, b: CalendarSidebarTimedEventLike) {
    return a.startsAt < b.endsAt && a.endsAt > b.startsAt;
}
